import { useState, useEffect } from "react";
import { runQuery } from "../services/api";
import DataTable from "./DataTable";
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const DEFAULT_COLORS = ["var(--accent)", "#22c55e", "#f59e0b", "#a855f7", "#ef4444", "#06b6d4"];

const tooltipStyle = {
  contentStyle: { background: "var(--bg-page)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "var(--text-primary)" },
  itemStyle: { color: "var(--text-secondary)" },
};

function getColor(col, colorMap, index) {
  return colorMap?.[col] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
}

function buildChartData(rows, xCol) {
  return rows.map((row) => {
    const entry = { _x: row[xCol] !== null && row[xCol] !== undefined ? String(row[xCol]) : "" };
    Object.keys(row).forEach((k) => {
      if (k !== xCol) entry[k] = row[k] !== null && row[k] !== undefined ? Number(row[k]) : null;
    });
    return entry;
  });
}

function StatCards({ rows, numericCols, colorMap }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
      {numericCols.map((col, i) => {
        const vals = rows.map((r) => Number(r[col])).filter((v) => !isNaN(v));
        const total = vals.reduce((a, b) => a + b, 0);
        const max = Math.max(...vals);
        const color = getColor(col, colorMap, i);
        return (
          <div key={col} style={{ background: "var(--bg-card)", borderRadius: 10, padding: "14px 16px", borderLeft: `4px solid ${color}` }}>
            <div style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {col.replace(/_/g, " ")}
            </div>
            <div style={{ color: "var(--text-heading)", fontSize: 20, fontWeight: 700 }}>{fmtNum(total)}</div>
            <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>Maks: {fmtNum(max)}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function ReportView({ report }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("grafik");

  useEffect(() => {
    setResult(null);
    setError(null);
    setLoading(true);
    runQuery(report.sql)
      .then((r) => setResult(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [report.id]);

  const { xCol, chartType = "both", colorMap = {} } = report.chartConfig;

  const numericCols = result
    ? result.columns.filter((c) => c !== xCol && result.rows.some((r) => typeof r[c] === "number" || !isNaN(Number(r[c]))))
    : [];

  const chartData = result ? buildChartData(result.rows, xCol) : [];

  return (
    <div>
      {/* Başlık */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "var(--text-heading)" }}>
          {report.icon} {report.name}
        </div>
        <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>{report.description}</div>
      </div>

      {loading && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text-muted)", fontSize: 14, padding: "32px 0" }}>
          <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⏳</span>
          Veri yükleniyor...
        </div>
      )}

      {error && (
        <div style={{ background: "#450a0a", border: "1px solid #7f1d1d", borderRadius: 8, padding: "12px 16px", color: "#fca5a5", fontSize: 13 }}>
          {error}
        </div>
      )}

      {result && !loading && (
        <>
          {/* Özet Kartlar */}
          <StatCards rows={result.rows} numericCols={numericCols} colorMap={colorMap} />

          {/* Sekme bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid var(--border)", marginBottom: 16 }}>
            <span style={{ color: "var(--text-faint)", fontSize: 13 }}>
              {result.row_count} satır · {result.columns.length} kolon
            </span>
            <div style={{ display: "flex" }}>
              {["grafik", "tablo"].map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveTab(t)}
                  style={{
                    padding: "8px 18px",
                    background: "transparent",
                    border: "none",
                    borderBottom: activeTab === t ? "2px solid var(--accent)" : "2px solid transparent",
                    color: activeTab === t ? "var(--accent)" : "var(--text-muted)",
                    cursor: "pointer",
                    fontWeight: activeTab === t ? 600 : 400,
                    fontSize: 14,
                  }}
                >
                  {t === "grafik" ? "📊 Grafik" : "📋 Tablo"}
                </button>
              ))}
            </div>
          </div>

          {/* Tablo */}
          {activeTab === "tablo" && (
            <DataTable
              columns={result.columns}
              rows={result.rows}
              total={result.row_count}
              limit={result.row_count}
              offset={0}
            />
          )}

          {/* Grafikler */}
          {activeTab === "grafik" && (
            <div>
              {/* Line Chart */}
              {(chartType === "line" || chartType === "both") && (
                <div style={{ background: "var(--bg-card)", borderRadius: 10, padding: "18px 20px", marginBottom: 18 }}>
                  <div style={{ color: "var(--text-secondary)", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
                    Aylık Trend
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData} margin={{ top: 0, right: 20, bottom: 44, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis
                        dataKey="_x"
                        tick={{ fill: "var(--text-muted)", fontSize: 11 }}
                        angle={-40}
                        textAnchor="end"
                      />
                      <YAxis tick={{ fill: "var(--text-muted)", fontSize: 11 }} tickFormatter={fmtAxis} />
                      <Tooltip {...tooltipStyle} formatter={(v, name) => [fmtNum(v), name.replace(/_/g, " ")]} />
                      <Legend wrapperStyle={{ color: "var(--text-secondary)", fontSize: 12, paddingTop: 8 }} formatter={(v) => v.replace(/_/g, " ")} />
                      {numericCols.map((col, i) => (
                        <Line
                          key={col}
                          type="monotone"
                          dataKey={col}
                          stroke={getColor(col, colorMap, i)}
                          strokeWidth={2.5}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Bar Chart */}
              {(chartType === "bar" || chartType === "both") && (
                <div style={{ background: "var(--bg-card)", borderRadius: 10, padding: "18px 20px" }}>
                  <div style={{ color: "var(--text-secondary)", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
                    Aylık Karşılaştırma
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} margin={{ top: 0, right: 20, bottom: 44, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis
                        dataKey="_x"
                        tick={{ fill: "var(--text-muted)", fontSize: 11 }}
                        angle={-40}
                        textAnchor="end"
                      />
                      <YAxis tick={{ fill: "var(--text-muted)", fontSize: 11 }} tickFormatter={fmtAxis} />
                      <Tooltip {...tooltipStyle} formatter={(v, name) => [fmtNum(v), name.replace(/_/g, " ")]} />
                      <Legend wrapperStyle={{ color: "var(--text-secondary)", fontSize: 12, paddingTop: 8 }} formatter={(v) => v.replace(/_/g, " ")} />
                      {numericCols.map((col, i) => (
                        <Bar key={col} dataKey={col} fill={getColor(col, colorMap, i)} radius={[4, 4, 0, 0]} maxBarSize={36} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function fmtNum(val) {
  if (val === null || val === undefined || isNaN(val)) return "—";
  const n = parseFloat(val);
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtAxis(val) {
  if (Math.abs(val) >= 1_000_000) return (val / 1_000_000).toFixed(1) + "M";
  if (Math.abs(val) >= 1_000) return (val / 1_000).toFixed(0) + "K";
  return val;
}
